import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  X,
  XCircle,
} from "lucide-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { RestaurantSettlementService } from "../../services";
import { currency, dateTime } from "../../utils/format";

const money = (value) => currency(Number(value || 0));

const PRIMARY_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-900 disabled:cursor-not-allowed disabled:opacity-60";

const OUTLINE_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const formatPeriod = (row) => {
  if (row.businessDate) {
    return `Business Date: ${row.businessDate}`;
  }

  return `${dateTime(row.periodStart)} - ${dateTime(row.periodEnd)}`;
};

const Dialog = ({ dialog, loading, onClose }) => {
  if (!dialog.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {dialog.title}
            </h3>
            {dialog.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{dialog.subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">{dialog.body}</div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={OUTLINE_BUTTON_CLASS}
          >
            {dialog.cancelLabel || "Close"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span
      className={
        bold
          ? "font-display font-semibold text-slate-900"
          : "text-slate-800"
      }
    >
      {value}
    </span>
  </div>
);

const NoteForm = ({
  label,
  placeholder,
  required = false,
  danger = false,
  onSubmit,
}) => {
  const [value, setValue] = useState("");

  const canSubmit = !required || value.trim();

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={3}
          className={`mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ${
            danger
              ? "focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
              : "focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          }`}
          placeholder={placeholder}
        />
      </div>

      <button
        type="button"
        onClick={() => onSubmit(value.trim())}
        disabled={!canSubmit}
        className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 ${
          danger
            ? "bg-rose-600 hover:bg-rose-700"
            : "bg-emerald-600 hover:bg-emerald-700"
        }`}
      >
        Continue
      </button>
    </div>
  );
};

const Pagination = ({
  totalItems,
  page,
  pageSize,
  setPage,
  setPageSize,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(totalItems, (page + 1) * pageSize);

  const canPrevious = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="text-slate-500">Rows per page</span>

        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>

        <span className="text-slate-500">
          {start}–{end} of {totalItems}
        </span>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setPage(0)}
          disabled={!canPrevious}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          «
        </button>

        <button
          type="button"
          onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          disabled={!canPrevious}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ‹
        </button>

        <span className="px-2 text-slate-700">
          Page {page + 1} / {totalPages}
        </span>

        <button
          type="button"
          onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
          disabled={!canNext}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ›
        </button>

        <button
          type="button"
          onClick={() => setPage(totalPages - 1)}
          disabled={!canNext}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          »
        </button>
      </div>
    </div>
  );
};

export const RestaurantSettlementsPage = () => {
  const [searchParams] = useSearchParams();
  const { role } = useAuth();
  const businessDate = searchParams.get("businessDate") || undefined;
  const canDecide = role === "RES_CASHIER";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");

  const [filters, setFilters] = useState({
    status: "ALL",
    search: "",
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [dialog, setDialog] = useState({
    open: false,
  });

  const closeDialog = () => {
    if (actionLoadingKey) {
      return;
    }

    setDialog({ open: false });
  };

  const loadRows = useCallback(async () => {
    setLoading(true);

    try {
      const data = await RestaurantSettlementService.list(businessDate);
      setRows(data || []);
    } catch (error) {
      setDialog({
        open: true,
        title: "Unable to load settlements",
        body: (
          <div className="flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <p>
              {error?.response?.data?.message ||
                error?.message ||
                "Please refresh and try again."}
            </p>
          </div>
        ),
        cancelLabel: "OK",
      });
    } finally {
      setLoading(false);
    }
  }, [businessDate]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return (rows || []).filter((row) => {
      if (filters.status !== "ALL" && row.status !== filters.status) {
        return false;
      }

      const searchable = [
        row.cashierFullName,
        row.cashierUsername,
        row.businessDate,
        row.status,
        row.cashierNote,
        row.adminNote,
        row.paidAmount,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return search === "" || searchable.includes(search);
    });
  }, [rows, filters]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const pendingRows = useMemo(
    () => filteredRows.filter((row) => row.status === "PENDING"),
    [filteredRows]
  );

  const confirmedRows = useMemo(
    () => filteredRows.filter((row) => row.status === "CONFIRMED"),
    [filteredRows]
  );

  const rejectedRows = useMemo(
    () => filteredRows.filter((row) => row.status === "REJECTED"),
    [filteredRows]
  );

  const confirmedTotal = useMemo(
    () =>
      confirmedRows.reduce(
        (sum, row) => sum + Number(row.paidAmount || 0),
        0
      ),
    [confirmedRows]
  );

  const openDetails = (row) => {
    setDialog({
      open: true,
      title: "Bar → Restaurant Payment",
      subtitle: `Requested by ${row.cashierFullName || row.cashierUsername}`,
      body: (
        <div className="space-y-3 text-sm">
          <Row
            label="Cashier"
            value={row.cashierFullName || row.cashierUsername || "-"}
          />
          <Row label="Business Date" value={row.businessDate || "-"} />
          <Row label="From" value={dateTime(row.periodStart)} />
          <Row label="To" value={dateTime(row.periodEnd)} />
          <Row label="Amount" value={money(row.paidAmount)} bold />
          <Row label="Status" value={row.status} />
          {row.cashierNote && (
            <Row label="Cashier note" value={row.cashierNote} />
          )}
          {row.adminNote && <Row label="Note" value={row.adminNote} />}
        </div>
      ),
      cancelLabel: "Close",
    });
  };

  const confirmSettlement = (row) => {
    setDialog({
      open: true,
      title: "Confirm Cash Received",
      subtitle: "Confirm only after the bar has physically paid this amount.",
      body: (
        <NoteForm
          label="Note optional"
          placeholder="Example: Cash received"
          onSubmit={async (note) => {
            setActionLoadingKey(`confirm-${row.settlementId}`);

            try {
              await RestaurantSettlementService.confirm(
                row.settlementId,
                note
              );

              setDialog({
                open: true,
                title: "Payment confirmed",
                body: (
                  <div className="flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                    <CheckCircle2 className="h-5 w-5 shrink-0" />
                    <p>The restaurant cash payment was confirmed.</p>
                  </div>
                ),
                cancelLabel: "OK",
              });

              await loadRows();
            } catch (error) {
              setDialog({
                open: true,
                title: "Confirm failed",
                body: (
                  <div className="flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    <AlertTriangle className="h-5 w-5 shrink-0" />
                    <p>
                      {error?.response?.data?.message ||
                        error?.message ||
                        "Could not confirm this payment."}
                    </p>
                  </div>
                ),
                cancelLabel: "OK",
              });
            } finally {
              setActionLoadingKey("");
            }
          }}
        />
      ),
      cancelLabel: "Close",
    });
  };

  const rejectSettlement = (row) => {
    setDialog({
      open: true,
      title: "Reject Payment",
      subtitle: "Reject if the bar payment was not received or amount is wrong.",
      body: (
        <NoteForm
          label="Reject reason"
          placeholder="Reason for rejecting"
          required
          danger
          onSubmit={async (reason) => {
            setActionLoadingKey(`reject-${row.settlementId}`);

            try {
              await RestaurantSettlementService.reject(
                row.settlementId,
                reason
              );

              setDialog({
                open: true,
                title: "Payment rejected",
                body: (
                  <div className="flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                    <CheckCircle2 className="h-5 w-5 shrink-0" />
                    <p>The restaurant cash payment was rejected.</p>
                  </div>
                ),
                cancelLabel: "OK",
              });

              await loadRows();
            } catch (error) {
              setDialog({
                open: true,
                title: "Reject failed",
                body: (
                  <div className="flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    <AlertTriangle className="h-5 w-5 shrink-0" />
                    <p>
                      {error?.response?.data?.message ||
                        error?.message ||
                        "Could not reject this payment."}
                    </p>
                  </div>
                ),
                cancelLabel: "OK",
              });
            } finally {
              setActionLoadingKey("");
            }
          }}
        />
      ),
      cancelLabel: "Close",
    });
  };

  return (
    <div>
      <PageHeader
        title="Restaurant Settlements"
        subtitle="Confirm or reject cash payments received from the bar."
        actions={
          <button
            type="button"
            onClick={loadRows}
            disabled={loading}
            className={PRIMARY_BUTTON_CLASS}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Pending From Bar"
          value={loading ? "…" : pendingRows.length}
          icon="Receipt"
          tone="gold"
        />

        <StatCard
          label="Confirmed Payments"
          value={loading ? "…" : confirmedRows.length}
          icon="BadgeDollarSign"
          tone="emerald"
        />

        <StatCard
          label="Rejected Payments"
          value={loading ? "…" : rejectedRows.length}
          icon="Receipt"
          tone="rose"
        />

        <StatCard
          label="Confirmed Amount"
          value={loading ? "…" : money(confirmedTotal)}
          icon="Banknote"
          tone="navy"
        />
      </div>

      <div className="bms-card mt-6 p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">
              Bar → Restaurant Payments
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Confirm only after cash has been received from the bar.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[170px_260px]">
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  status: event.target.value,
                }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
                className="w-full bg-transparent text-sm outline-none"
                placeholder="Search"
              />
            </div>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Requested
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Cashier
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Period / Date
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">
                  Amount
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {pagedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    No restaurant cash settlement records found.
                  </td>
                </tr>
              ) : (
                pagedRows.map((row) => (
                  <tr key={row.settlementId}>
                    <td className="px-4 py-3 text-slate-700">
                      {dateTime(row.requestedAt)}
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {row.cashierFullName || row.cashierUsername || "-"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {row.cashierUsername || "-"}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-slate-700">
                      {formatPeriod(row)}
                    </td>

                    <td className="px-4 py-3 text-right font-semibold">
                      {money(row.paidAmount)}
                    </td>

                    <td className="px-4 py-3">
                      <StatusBadge value={row.status} />
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openDetails(row)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </button>

                        {canDecide && row.status === "PENDING" && (
                          <>
                            <button
                              type="button"
                              onClick={() => confirmSettlement(row)}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Confirm
                            </button>

                            <button
                              type="button"
                              onClick={() => rejectSettlement(row)}
                              className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                            >
                              <XCircle className="h-3 w-3" />
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          totalItems={filteredRows.length}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </div>

      <Dialog
        dialog={dialog}
        loading={Boolean(actionLoadingKey)}
        onClose={closeDialog}P
      />
    </div>
  );
};
